import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ShoppingCart,
  Download,
  Heart,
  Package,
  CreditCard,
  Eye,
  Calendar,
  Image,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Star
} from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useCart } from '@/hooks/useCart';
import { getApiBaseUrl } from '@/config/production';

interface AthleteStats {
  totalPurchases: number;
  totalSpent: number;
  favoritePhotos: number;
  cartItems: number;
  downloadedPhotos: number;
}

interface PurchasedPhoto {
  id: string;
  filename: string;
  eventName: string;
  eventDate: string;
  purchaseDate: string;
  price: number;
  downloadUrl?: string;
  thumbnailUrl: string;
  status: 'processing' | 'available' | 'expired';
}

interface Order {
  id: string;
  orderNumber: string;
  date: string;
  status: 'pending' | 'completed' | 'cancelled';
  total: number;
  itemsCount: number;
  photos: PurchasedPhoto[];
}

interface CartItem {
  id: string;
  photoId: string;
  filename: string;
  eventName: string;
  price: number;
  thumbnailUrl: string;
}

export default function AthleteDashboard() {
  const [, setLocation] = useLocation();
  const { user, logout } = useUser();
  const { cart, totalItems, totalAmount, error: cartError, isLoading: cartLoading } = useCart();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<AthleteStats>({
    totalPurchases: 0,
    totalSpent: 0,
    favoritePhotos: 0,
    cartItems: 0,
    downloadedPhotos: 0
  });
  const [purchasedPhotos, setPurchasedPhotos] = useState<PurchasedPhoto[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);

  // Verificar se o usuário está logado
  useEffect(() => {
    if (!user) {
      setLocation('/login');
      return;
    }

    loadDashboardData();
  }, [user, setLocation]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Simular dados do dashboard (substituir por chamadas reais da API)
      await new Promise(resolve => setTimeout(resolve, 1000));

      setStats({
        totalPurchases: 23,
        totalSpent: 575.00,
        favoritePhotos: 12,
        cartItems: cart?.items?.length || 0,
        downloadedPhotos: 18
      });

      setPurchasedPhotos([
        {
          id: '1',
          filename: 'maratona_sp_001.jpg',
          eventName: 'Maratona de São Paulo 2024',
          eventDate: '2024-01-20T06:00:00Z',
          purchaseDate: '2024-01-21T10:30:00Z',
          price: 25.00,
          downloadUrl: '/downloads/maratona_sp_001.jpg',
          thumbnailUrl: '/thumbnails/maratona_sp_001.jpg',
          status: 'available'
        },
        {
          id: '2',
          filename: 'trail_run_002.jpg',
          eventName: 'Trail Run Serra da Mantiqueira',
          eventDate: '2024-01-25T07:00:00Z',
          purchaseDate: '2024-01-26T14:20:00Z',
          price: 25.00,
          thumbnailUrl: '/thumbnails/trail_run_002.jpg',
          status: 'processing'
        }
      ]);

      setOrders([
        {
          id: '1',
          orderNumber: 'ORD-2024-001',
          date: '2024-01-21T10:30:00Z',
          status: 'completed',
          total: 75.00,
          itemsCount: 3,
          photos: []
        },
        {
          id: '2',
          orderNumber: 'ORD-2024-002',
          date: '2024-01-26T14:20:00Z',
          status: 'pending',
          total: 50.00,
          itemsCount: 2,
          photos: []
        }
      ]);

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setError('Erro ao carregar dados do dashboard. Tente novamente.');
      // Adicionar tratamento de erro mais robusto
      setStats({
        totalPurchases: 0,
        totalSpent: 0,
        favoritePhotos: 0,
        cartItems: cart?.items?.length || 0,
        downloadedPhotos: 0
      });
      setPurchasedPhotos([]);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge variant="default" className="bg-primary"><CheckCircle className="w-3 h-3 mr-1" />Disponível</Badge>;
      case 'processing':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Processando</Badge>;
      case 'expired':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Expirado</Badge>;
      case 'completed':
        return <Badge variant="default" className="bg-primary"><CheckCircle className="w-3 h-3 mr-1" />Concluído</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'cancelled':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Cancelado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleDownload = (photo: PurchasedPhoto) => {
    if (photo.downloadUrl && photo.status === 'available') {
      // Implementar download da foto
      window.open(photo.downloadUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="h-12 w-12 animate-pulse mx-auto mb-4 text-primary" />
          <p className="text-lg">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || cartError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold mb-2 text-gray-800">Erro ao carregar dados</h2>
          <p className="text-gray-600 mb-4">{error || cartError}</p>
          <Button
            onClick={() => {
              setError(null);
              loadDashboardData();
            }}
            className="bg-primary hover:bg-secondary"
          >
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-primary to-secondary text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Minha Biblioteca</h1>
              <p className="text-sm opacity-80">
                Bem-vindo, {user?.name || user?.email}! Gerencie suas fotos e pedidos.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setLocation('/cart')}
                className="bg-white text-primary hover:bg-gray-100"
                disabled={cartLoading}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {cartLoading ? 'Carregando...' : `Carrinho (${cart?.items?.length || 0})`}
              </Button>
              <Button
                onClick={() => setLocation('/favorites')}
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-primary"
              >
                <Heart className="w-4 h-4 mr-2" />
                Favoritos
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Fotos Compradas</CardTitle>
              <Image className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalPurchases}</div>
              <p className="text-xs text-muted-foreground">
                {stats.downloadedPhotos} já baixadas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Gasto</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R$ {stats.totalSpent.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">
                Média de R$ {(stats.totalSpent / Math.max(stats.totalPurchases, 1)).toFixed(2)} por foto
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Favoritos</CardTitle>
              <Heart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.favoritePhotos}</div>
              <p className="text-xs text-muted-foreground">
                Fotos salvas para depois
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Carrinho</CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.cartItems}</div>
              <p className="text-xs text-muted-foreground">
                R$ {totalAmount.toFixed(2)} total
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs de Conteúdo */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="photos">Minhas Fotos</TabsTrigger>
            <TabsTrigger value="orders">Pedidos</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Fotos Recentes */}
              <Card>
                <CardHeader>
                  <CardTitle>Fotos Recentes</CardTitle>
                  <CardDescription>Suas últimas fotos compradas</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {purchasedPhotos.slice(0, 3).map((photo) => (
                      <div key={photo.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Image className="w-6 h-6 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{photo.filename}</p>
                            <p className="text-xs text-muted-foreground">{photo.eventName}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(photo.status)}
                          {photo.status === 'available' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-1"
                              onClick={() => handleDownload(photo)}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Baixar
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Pedidos Recentes */}
              <Card>
                <CardHeader>
                  <CardTitle>Pedidos Recentes</CardTitle>
                  <CardDescription>Histórico dos seus pedidos</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {orders.slice(0, 3).map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{order.orderNumber}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(order.date).toLocaleDateString('pt-BR')} • {order.itemsCount} itens
                          </p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(order.status)}
                          <p className="text-sm font-medium mt-1">
                            R$ {order.total.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Ações Rápidas */}
            <Card>
              <CardHeader>
                <CardTitle>Ações Rápidas</CardTitle>
                <CardDescription>Acesse rapidamente as principais funcionalidades</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    onClick={() => setLocation('/events')}
                    className="h-20 flex flex-col items-center justify-center"
                  >
                    <Calendar className="w-6 h-6 mb-2" />
                    Buscar Eventos
                  </Button>
                  <Button
                    onClick={() => setLocation('/favorites')}
                    variant="outline"
                    className="h-20 flex flex-col items-center justify-center"
                  >
                    <Heart className="w-6 h-6 mb-2" />
                    Ver Favoritos
                  </Button>
                  <Button
                    onClick={() => setLocation('/cart')}
                    variant="outline"
                    className="h-20 flex flex-col items-center justify-center"
                  >
                    <ShoppingCart className="w-6 h-6 mb-2" />
                    Finalizar Compra
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="photos" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Minhas Fotos Compradas</CardTitle>
                <CardDescription>Todas as fotos que você adquiriu</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {purchasedPhotos.map((photo) => (
                    <div key={photo.id} className="border rounded-lg p-4">
                      <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center mb-3">
                        <Image className="w-8 h-8 text-gray-500" />
                      </div>
                      <div className="space-y-2">
                        <p className="font-medium text-sm">{photo.filename}</p>
                        <p className="text-xs text-muted-foreground">{photo.eventName}</p>
                        <div className="flex items-center justify-between">
                          {getStatusBadge(photo.status)}
                          <span className="text-sm font-medium">R$ {photo.price.toFixed(2)}</span>
                        </div>
                        {photo.status === 'available' && (
                          <Button
                            size="sm"
                            className="w-full"
                            onClick={() => handleDownload(photo)}
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Baixar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="orders" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Pedidos</CardTitle>
                <CardDescription>Todos os seus pedidos realizados</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium">{order.orderNumber}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(order.date).toLocaleDateString('pt-BR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(order.status)}
                          <p className="text-lg font-bold mt-1">R$ {order.total.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{order.itemsCount} itens</span>
                        <Button variant="outline" size="sm">
                          <Eye className="w-3 h-3 mr-1" />
                          Ver Detalhes
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}